import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import ProductImageComponent from "./imgs/ProductImage";
import "../styles/product_page.css";
import { CONDITION, STATE } from "../classes/Enum";
import { normaliseVariantName } from "../helper/addToCart";
import "../styles/pokemon_type.css";
import '../styles/iteminfo.css';
import { addToCart, getCartQuantity } from '../helper/main';
import { useAppState, useCart, useSearch } from "./Context";
import { getDisplayPrice, canBuy, getStatus, getStockLevel } from "../helper/products";
import { FRONT_URL } from "../config";
import { toggleWish } from "../helper/main";
import StockIndicator, { StockStatusIndicator } from "./StockDisplay";
import { WishlistIcon } from "../icons/heart";
import { CartIcon } from "../icons/cart";
import { CONDITION_GUIDE, getConditionGuide } from "../helper/conditionGuide";
import { getProductNameParts } from "../helper/productName";
import ProductName, { ProductNameDisplay } from "./ProductName";
import ConditionQuizOverlay from "./ConditionQuiz";
import { preloadAdjacentProductImages } from "../helper/image";

function getStockStatus(item) {
  if (item.canBuy) {
      let stockLevel = getStockLevel(item.quantity, item.type);
      if (stockLevel === "IN") {
        return "In Stock";
      } else if (stockLevel === "LOW") {
        return "Only " + item.quantity + " left";
      }

  }
  return "UNAVAILABLE";
}

function getItemInfoConditionLabel(code) {
  const entry = getConditionGuide(code);
  if (entry?.name) return entry.name.toUpperCase();
  return String(code || "").replace(/_/g, " ").toUpperCase();
}

const HistoryGraph = ({ item, variant, condition }) => {
  const [range, setRange] = useState("week");

  const data = useMemo(() => generateData(range), [range]);

  const width = 900;
  const height = 320;
  const padding = 32;

  const values = data.map((d) => d.value);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const valueRange = Math.max(max - min, 1);

  const xStep =
    data.length > 1 ? (width - padding * 2) / (data.length - 1) : 0;

  const points = data
    .map((point, index) => {
      const x = padding + index * xStep;
      const y =
        height -
        padding -
        ((point.value - min) / valueRange) * (height - padding * 2);

      return `${x},${y}`;
    })
    .join(" ");

  const areaPoints = `
    ${padding},${height - padding}
    ${points}
    ${width - padding},${height - padding}
  `;

  return (
    <div
      className="history-graph-container"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 30, fontWeight: 700 }}>History</div>
          <div style={{ fontSize: 14, opacity: 0.7 }}>
            Price history for {item.name} {variant && `- ${normaliseVariantName(variant)}`}{" "}
            {condition && `(${condition})`}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            background: "#1a1a1a",
            padding: 6,
            borderRadius: 999,
          }}
        >
          <RangeButton
            label="1 Week"
            active={range === "week"}
            onClick={() => setRange("week")}
          />
          <RangeButton
            label="1 Month"
            active={range === "month"}
            onClick={() => setRange("month")}
          />
          <RangeButton
            label="6 Months"
            active={range === "sixMonths"}
            onClick={() => setRange("sixMonths")}
          />
        </div>
      </div>

      <div
        style={{
          width: "100%",
          overflowX: "auto",
          borderRadius: 16,
          background: "#0b0b0b",
          padding: 12,
        }}
      >
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          {[0, 1, 2, 3, 4].map((line) => {
            const y =
              padding + ((height - padding * 2) / 4) * line;

            return (
              <line
                key={line}
                x1={padding}
                y1={y}
                x2={width - padding}
                y2={y}
                stroke="#262626"
                strokeWidth="1"
              />
            );
          })}

          <polygon
            points={areaPoints}
            fill="rgba(120, 120, 255, 0.14)"
          />

          <polyline
            fill="none"
            stroke="#7c7cff"
            strokeWidth="4"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={points}
          />

          {data.map((point, index) => {
            const x = padding + index * xStep;
            const y =
              height -
              padding -
              ((point.value - min) / valueRange) * (height - padding * 2);

            return (
              <g key={point.label}>
                <circle cx={x} cy={y} r="5" fill="#a78bfa" />
                <text
                  x={x}
                  y={height - 10}
                  textAnchor="middle"
                  fontSize="20"
                  fill="#999"
                >
                  {point.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function RangeButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`history-graph-button ${active ? 'active' : ''}`}
    >
      {label}
    </button>
  );
}

function generateData(range) {
  const config = {
    week: { count: 7, labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] },
    month: {
      count: 30,
      labels: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    },
    sixMonths: {
      count: 6,
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    },
  };

  const selected = config[range];

  return selected.labels.map((label, index) => ({
    label,
    value: makeRandomValue(index, range),
  }));
}

function makeRandomValue(index, range) {
  const base =
    range === "week" ? 40 : range === "month" ? 60 : 120;

  const variance =
    range === "week" ? 30 : range === "month" ? 40 : 70;

  return Math.max(
    5,
    Math.round(base + Math.sin(index * 0.8) * variance + Math.random() * variance)
  );
}

const BackButton = () => {
  return (
    <div className="back-button">
      <svg
        width="32"
        height="32"
        viewBox="0 0 128 128"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="backGradient" x1="20" y1="64" x2="108" y2="64">
            <stop className="grad1" offset="0%" />
            <stop className="grad2" offset="35%" />
            <stop className="grad3" offset="65%" />
            <stop className="grad4" offset="100%" />
          </linearGradient>
        </defs>

        <path
          d="M82 24L42 64L82 104M46 64H102"
          stroke="url(#backGradient)"
          strokeWidth="12"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

function calculatePrice(selectedItem, condition, variant) {
  const currentVariant = selectedItem.conditionQuantities?.[variant] || {};
  const conditionData = currentVariant?.[condition];

  const rawPrice = conditionData?.price ?? selectedItem.price;
  return Number(rawPrice); // Ensure it's a number
};

const ProductImage = ({tiltRef, selectedItem, setSelectedItem}) => {
  return <div className="card-image-container">
        <p style={{opacity:1, color:"grey"}}>Illustration for reference only</p>
    <p style={{opacity:1, color: 'rgb(201, 201, 201)'}}>Hold the card to interact!</p>
    <div ref={tiltRef}>
      <ProductImageComponent item={selectedItem} setSelectedItem={setSelectedItem} size="large" />
    </div>
  </div>
}

// Shared by ProductDetails and ProductButtons: the buttons act on the variant
// and condition the user picked in the details panel.
function useProductPurchase({ selectedItem, isWished, setWishlist }) {
  const isCard = selectedItem?.type === STATE.CARD;
  const [clickedShared, setClickShared] = useState(false);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${FRONT_URL}products/id/${selectedItem.id}`);
      setClickShared(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Compute defaults safely
  const defaults = useMemo(() => {
    if (!isCard) return { variant: "", condition: "" };

    const cq = selectedItem?.conditionQuantities || {};
    const variants = Object.keys(cq);
    const defaultVariant = variants[0];

    const conditionsObj = cq[defaultVariant];
    const defaultCondition = conditionsObj
      ? Object.keys(conditionsObj).find(
          key => conditionsObj[key]?.quantity > 0
        ) || ""
      : "";

    return { variant: defaultVariant, condition: defaultCondition };
  }, [isCard, selectedItem]);

  const [variant, setVariant] = useState(defaults.variant);
  const [condition, setCondition] = useState(defaults.condition);
  const {cart, setCart} = useCart();
  // Reset on item change
  useEffect(() => {
    setVariant(defaults.variant);
    setCondition(defaults.condition);
  }, [defaults.variant, defaults.condition]);

  // Guard current selection (variant/condition might become invalid after updates)
  const selectedCQ = isCard ? (selectedItem?.conditionQuantities || {}) : null;
  const safeVariant = isCard && selectedCQ[variant] ? variant : defaults.variant;
  const safeCondition =
    isCard && selectedCQ?.[safeVariant]?.[condition]
      ? condition
      : (Object.keys(selectedCQ?.[safeVariant] || {})[0] || defaults.condition);

  const availableQty = isCard
    ? (selectedCQ?.[safeVariant]?.[safeCondition]?.quantity ?? 0)
    : (selectedItem?.quantity ?? 0);

  const wished = isWished(selectedItem.id);
  const canPurchase = availableQty > 0 && canBuy(selectedItem, condition, variant);
  const handleWishlistClick = () => toggleWish(wished, setWishlist, selectedItem.id);

  return {
    isCard,
    cart,
    setCart,
    clickedShared,
    handleShare,
    variant,
    setVariant,
    condition,
    setCondition,
    safeVariant,
    safeCondition,
    availableQty,
    wished,
    canPurchase,
    handleWishlistClick,
  };
}

const ProductDetails = ({ selectedItem, onOpenQuiz, purchase }) => {
  const {appState} = useAppState();
  const {
    isCard,
    cart,
    variant,
    setVariant,
    condition,
    setCondition,
    safeVariant,
    safeCondition,
    availableQty,
  } = purchase;

  const { titleName, cardNumber, language } = getProductNameParts(
    selectedItem.name,
    selectedItem.type
  );

  return (
    <div className="card-details">
      <div className="card-intro">
        {isCard ? (
            <ProductNameDisplay
              displayName={titleName}
              language={language}
              className={`card-title type-${selectedItem.pokemonType}`}
              stacked
            />
        ) : (
          <ProductName
            name={selectedItem.name}
            type={selectedItem.type}
            className="card-title"
            stacked
          />
        )}
        {isCard && <div className="card-subtitle card-header">
              {`${cardNumber || ""} `} {selectedItem.seriesName}{" "}
              {selectedItem.setName}
            </div>}

        <div className="card-header">
          {selectedItem.type !== STATE.CARD && <div className="card-subtitle">{selectedItem.type}</div>}
        </div>

      </div>
      <div style={{position:'relative'}}>
        <div className="card-info hide-scrollbar">

                {canBuy(selectedItem, condition, variant) && (
          <h1 className="item-price">
            {`$${getDisplayPrice(calculatePrice(selectedItem, safeCondition, safeVariant))}`}
          </h1>
        )}

        {![STATE.SINGLE, STATE.SLAB].includes(selectedItem.type) ? (
          <textarea
            className="info-box"
            readOnly
            value={(selectedItem.desc || "").replace(/\\n/g, "\n")}
          />
        ) :
          <VariantConditionDisplay
            selectedItem={selectedItem}
            condition={safeCondition}
            variant={safeVariant}
            setVariant={setVariant}
            setCondition={setCondition}
            onOpenQuiz={onOpenQuiz}
          />
      }

      {availableQty > 0 &&
        <div className="item-quantity in-cart">
              <StockStatusIndicator quantity={availableQty} type={appState}/>
              <span >{getStockStatus(selectedItem)}</span>
              {getCartQuantity(cart, selectedItem.id) > 0 && (
                <>
                  <span>|</span>
                  <span>{getCartQuantity(cart, selectedItem.id)} in cart</span>
                </>
              )}

          </div>
      }
          {/* <HistoryGraph item={selectedItem} variant={variant} condition={condition}/> */}
          <div className="copyright-spacing"/> 
        </div>
        {/* <div className="hug-bottom blur"> </div> */}

      </div>

    </div>
  );
};

const ProductButtons = ({ selectedItem, purchase }) => {
  const {
    cart,
    setCart,
    safeVariant,
    safeCondition,
    canPurchase,
    wished,
    clickedShared,
    handleWishlistClick,
    handleShare,
  } = purchase;

  return (
    <div className="product-buttons">
      <div className="iteminfo-buttons">
        {canPurchase ? (
        <button
          className="add-to-cart is-active"
          aria-label="Add to cart"
          title="Add to cart"
          onClick={() => addToCart(cart, setCart, selectedItem, safeVariant, safeCondition)}
        >
          <span className="mobile-only-button-icon"><CartIcon /></span>
          <span className="iteminfo-button-label">Add to cart</span>
        </button>
        ) : (
        <button
          className="add-to-cart is-active add-to-cart-wishlist"
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          title={wished ? "Wished" : "Wish"}
          onClick={handleWishlistClick}
        >
          <WishlistIcon active={wished} />
          <div className="iteminfo-button-info">
            <span>{wished ? "Wished!" : "Wish"}</span>
            <p>You will be notified when it&apos;s back in stock</p>
          </div>
        </button>
        )}
        <div className="secondary-button-bar">
          {canPurchase && (
          <button
            className="secondary-button secondary-active"
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            title={wished ? "Wished" : "Wish"}
            onClick={handleWishlistClick}
          >
          <WishlistIcon active={wished}/>
          <div className="iteminfo-button-info">
            <span>{wished ? "Wished!" : "Wish"}</span>
            <p>You will be notified when it&apos;s back in stock</p>
          </div>
          </button>
          )}
          <button
            className="tertiary-button tertiary-active"
            aria-label="Copy product link"
            title={clickedShared ? "Copied" : "Share"}
            onClick={handleShare}
          >
            <img className="secondary-button-symbol" src="/assets/imgs/share.png" alt="" />
            <div className="iteminfo-button-info">
              <span>{clickedShared ? "Copied!" : "Share"}</span>
              <p>Copy link to clipboard and share with friends</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};


function ConditionHelp({ onOpenQuiz }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  function openQuiz() {
    setOpen(false);
    onOpenQuiz?.();
  }

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div className="condition-help" ref={rootRef}>
      <button
        type="button"
        className="condition-help-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="What do card conditions mean?"
      >
        ?
      </button>
      {open && (
        <div className="condition-help-panel" role="dialog" aria-label="Condition guide">
          <p className="condition-help-title">Condition guide</p>
          <ul className="condition-help-list">
            {CONDITION_GUIDE.map((entry) => (
              <li key={entry.code}>
                <span className="condition-help-code">{entry.code}</span>
                <span className="condition-help-name">{entry.name}</span>
                <p className="condition-help-desc">{entry.description}</p>
              </li>
            ))}
          </ul>
          <button type="button" className="condition-help-quiz-link" onClick={openQuiz}>
            Take the condition quiz →
          </button>
        </div>
      )}
    </div>
  );
}

const VariantConditionDisplay = ({ selectedItem, condition, variant, setVariant, setCondition, onOpenQuiz }) => {


  const variants = Object.keys(selectedItem.conditionQuantities ? selectedItem.conditionQuantities : []);
  const hasVariants = variants.length > 0;

  return (
    <div className="condition-selector">
      {hasVariants && (
        <>
          <div className="condition-header">
            <label className="condition-label">Variant</label>
          </div>
          <div className="item-divider-long condition-section-divider" />
          <div className="variant-buttons">
            {variants.map((v, i) => (
              <button
                key={i}
                onClick={() => {
                  setVariant(v);
                  const firstCondition = Object.keys(selectedItem.conditionQuantities[v]).find(
                    key => selectedItem.conditionQuantities[v][key]?.quantity > 0
                  ) || ""
                  setCondition(firstCondition);
                }}
                className={`condition-button ${v === variant ? "secondary-active" : ""}`}
              >
                {normaliseVariantName(v)}
              </button>
            ))}
          </div>
        </>
      )}
      <div className="gap"></div>
        {hasVariants && (
          <>
            <div className="condition-header">
              <label className="condition-label">Condition</label>
              <ConditionHelp onOpenQuiz={onOpenQuiz} />
            </div>
            <div className="item-divider-long condition-section-divider" />
          </>
        )}
      <div className="variant-buttons">
        {hasVariants && Object.entries(selectedItem.conditionQuantities[variant]).map(([cond, data]) => (
          data.quantity > 0 &&
            <button
              key={cond}
              onClick={() => setCondition(cond)}
              className={`condition-button ${condition === cond ? "secondary-active" : ""}`}
            >
              {getItemInfoConditionLabel(cond)}
            </button>
        ))}
      </div>
    </div>
  );
};

const SWIPE_THRESHOLD_PX = 56;

const ItemInfoComponent = ({selectedItem, setSelectedItem, products, isWished, setWishlist}) => {
  const tiltRef = useRef(null);
  const swipeStartRef = useRef(null);
  const [focus, setFocus] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const purchase = useProductPurchase({ selectedItem, isWished, setWishlist });

  const navigate = useCallback((step) => {
    setSelectedItem((curr) => {
      if (!curr || !products?.length) return curr;
      const idx = products.findIndex(p => p.id === curr.id);
      if (idx === -1) return curr;

      const nextIdx = (idx + step + products.length) % products.length; // wrap
      return products[nextIdx];
    });
  }, [products, setSelectedItem]);

  useEffect(() => {
    preloadAdjacentProductImages(products, selectedItem?.id, 1, true);
  }, [products, selectedItem?.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") {
        if (quizOpen) {
          setQuizOpen(false);
        } else {
          setSelectedItem(null);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigate(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        navigate(1);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [navigate, setSelectedItem, quizOpen]);

  const onSwipeStart = (e) => {
    if (quizOpen || e.touches?.length !== 1) return;
    const touch = e.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const onSwipeEnd = (e) => {
    const start = swipeStartRef.current;
    swipeStartRef.current = null;
    if (!start || quizOpen || e.changedTouches?.length !== 1) return;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    // Prefer vertical scroll over product switching.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

    navigate(dx < 0 ? 1 : -1);
  };

  return (
    <div
      className="item-overlay"
      onTouchStart={onSwipeStart}
      onTouchEnd={onSwipeEnd}
    >
      <div onClick={() => setSelectedItem(null)}>
        <BackButton/>
      </div>
      <div className="product-info-background" onClick={() => setSelectedItem(null)} />
      <div className="product-info-page-layout">
        <button className="titter large" onClick={() => navigate(-1)}><h1>{"<<"}</h1></button>
        <div className="product">
          <div onClick={() => setFocus(!focus)}>
          <ProductImage selectedItem={selectedItem} setSelectedItem={setSelectedItem} tiltRef={tiltRef}/>
          </div>
          {!focus && (
            <div className="product-details-actions">
              <ProductDetails
                selectedItem={selectedItem}
                onOpenQuiz={() => setQuizOpen(true)}
                purchase={purchase}
              />
              <ProductButtons selectedItem={selectedItem} purchase={purchase} />
            </div>
          )}
        </div>
        <button className="titter-backwards large" onClick={() => navigate(1)}><h1>{">>"}</h1></button>
      </div>
      {quizOpen && <ConditionQuizOverlay onClose={() => setQuizOpen(false)} />}
    </div>
  );
};

export default ItemInfoComponent;
